import {
  fetchInstoreTemplatesAsync,
  instoreTemplatesSelectors,
} from "../features/instoreTemplates/instoreTemplatesSlice";
import { useAppDispatch, useAppSelector } from "../store/configureStore";
import { isWithinInterval, parse } from "date-fns";
import { useEffect } from "react";
import { InstoreTemplate } from "../models/instoreTemplate";

const STATIC_INSTORE_ORDER_TYPE_NAME = "Week";

// Returns the active "Week" instore templates whose date window covers today,
// one per instore type. There can be a separate Week template per instore type
// (= store_type); for each type we keep the most recent match (most recent
// `from`, tiebreak on most recent `created_at`). Multiple matches for the same
// type can occur with sloppy app usage.
export default function useActiveWeekInstoreTemplates(): InstoreTemplate[] {
  const dispatch = useAppDispatch();
  const { instoreTemplates_Loaded } = useAppSelector(
    (state) => state.instoreTemplates
  );
  const instoreTemplates = useAppSelector(instoreTemplatesSelectors.selectAll);

  useEffect(() => {
    if (!instoreTemplates_Loaded) {
      dispatch(fetchInstoreTemplatesAsync());
    }
  }, [instoreTemplates_Loaded, dispatch]);

  type Match = {
    template: InstoreTemplate;
    fromTime: number;
    createdAtTime: number;
  };

  const today = new Date();
  const matches = instoreTemplates
    .map<Match | null>((template) => {
      if (!template.active) return null;
      if (template.order_type_instore_name !== STATIC_INSTORE_ORDER_TYPE_NAME)
        return null;
      if (!template.from || !template.until) return null;
      const from = parse(template.from, "dd/MM/yyyy HH:mm", new Date());
      const until = parse(template.until, "dd/MM/yyyy HH:mm", new Date());
      if (isNaN(from.getTime()) || isNaN(until.getTime())) return null;
      if (!isWithinInterval(today, { start: from, end: until })) return null;
      const createdAt = template.created_at
        ? new Date(template.created_at).getTime()
        : 0;
      return {
        template,
        fromTime: from.getTime(),
        createdAtTime: isNaN(createdAt) ? 0 : createdAt,
      };
    })
    .filter((m): m is Match => m !== null);

  matches.sort((a, b) => {
    if (b.fromTime !== a.fromTime) return b.fromTime - a.fromTime;
    return b.createdAtTime - a.createdAtTime;
  });

  // Keep only the most recent match per instore type. `matches` is already
  // sorted most-recent-first, so the first one seen per type wins.
  const byType = new Map<number, InstoreTemplate>();
  for (const match of matches) {
    if (!byType.has(match.template.instore_type_id)) {
      byType.set(match.template.instore_type_id, match.template);
    }
  }

  return Array.from(byType.values());
}
